import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Block, DefaultText, MediumText, SmallText, Spinner } from '@guryou/html-components';
import { stateIsLoading, useResourcesNew } from '@guryou/redux';
import StaffCount from 'onboard/components/StaffCount';
import SelectableCubeGroup from 'onboard/components/StaffCube';
import CheckboxInfoContainer from 'onboard/containers/CheckboxInfoContainer';
import FormContainer from 'onboard/containers/FormContainer';
import InfoContainer from 'onboard/containers/InfoContainer';
import MainContainer from 'onboard/containers/MainContainer';
import PlayGuideVideoEmbed from 'onboard/containers/PlayGuideVideoEmbed';
import TitleContainer from 'onboard/containers/TitleContainer';
import { useOnboardContext } from 'onboard/context';
import { guideVideos } from 'onboard/GuideVideosUrls';
import StepButtons from '../StepButtons';

const CourseStaff = () => {
  const { t } = useTranslation();
  const { courseState, setCourseState, serviceType } = useOnboardContext();
  const [staffError, setStaffError] = useState(false);
  const [capacityError, setCapacityError] = useState('');
  const resources = useResourcesNew();
  const [maxCount, setMaxCount] = useState(Number(courseState.capacity));
  const [minCount, setMinCount] = useState(Number(courseState.minimumCapacity) || 'none');

  const handleMinChange = newMin => {
    setCapacityError('');
    if (typeof newMin === 'number' && newMin > maxCount) {
      setCapacityError(t('ONBOARD.minBiggerThanMax'));
      return;
    }
    setMinCount(newMin);
  };

  const handleMaxChange = newMax => {
    setCapacityError('');
    if (typeof newMax === 'number' && newMax < minCount) {
      setCapacityError(t('ONBOARD.maxSmallerThanMin'));
      return;
    }
    setMaxCount(newMax);
  };

  const handleSubmit = data => {
    const resourceIds = data.resources
      ?.split(',')
      .map(item => item.trim())
      .filter(item => item !== '')
      .map(Number);

    const err = resourceIds.length < 1;
    if (err) {
      setStaffError(true);
      return;
    }
    setCourseState({ ...data, resources: resourceIds });
  };

  const preselected = resources?.data && resources.data.length > 0 && [resources?.data[0].id];

  return (
    <MainContainer>
      <FormContainer handleSubmit={handleSubmit}>
        <TitleContainer title={t('ONBOARD.serviceStaffTitle')} />
        <Block lg={10} md={12} xs={12} margin={0} marginTop={6} flexWrap="wrap">
          {stateIsLoading(resources) ? (
            <Spinner />
          ) : (
            <SelectableCubeGroup
              error={staffError}
              setError={setStaffError}
              resources={resources?.data || []}
              values={courseState?.resources.length > 0 ? courseState?.resources : preselected}
            />
          )}
          <StaffCount name="capacity" count={maxCount} setCount={handleMaxChange} title={t('ONBOARD.maxCapacity')} />
          <StaffCount name="minimumCapacity" count={minCount} setCount={handleMinChange} allowNone={true} title={t('ONBOARD.minCapacity')} />
          {capacityError && (
            <SmallText color="red" marginTop={4}>
              {capacityError}
            </SmallText>
          )}
        </Block>
        <StepButtons />
      </FormContainer>
      <InfoContainer>
        <MediumText>{t('ONBOARD_TEXT.courseStaffTitle')}</MediumText>
        <DefaultText fontSize="1.2rem">{t('ONBOARD_TEXT.courseStaffSubtitle')}</DefaultText>
        <Block margin={0} marginTop={10}>
          <DefaultText fontSize="1.2rem" fontWeight="800">
            {t('ONBOARD_TEXT.courseStaffCheckboxTitle')}
          </DefaultText>
          <CheckboxInfoContainer text={t('ONBOARD_TEXT.courseStaffCheckbox1Text')} />
          <CheckboxInfoContainer text={t('ONBOARD_TEXT.courseStaffCheckbox2Text')} />
          <CheckboxInfoContainer text={t('ONBOARD_TEXT.courseStaffCheckbox3Text')} />
        </Block>
        <PlayGuideVideoEmbed videoId={guideVideos[`${serviceType}Staff`].videoId} modalTitle={guideVideos[`${serviceType}Staff`].title} />
      </InfoContainer>
    </MainContainer>
  );
};

export default CourseStaff;
